import { Injectable, inject } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { hasVariableToken } from '@app/_helpers/variable-tokens';
import { VariableScope, VariableService } from '@app/services/variable.service';
import { getVariables } from '@app/stores/variable/variable.selectors';
import { IVariableState } from '@app/stores/variable/variable.state';
import { SbTextConditionDialogComponent, SbTextConditionDialogResult } from './sb-text-condition-dialog.component';
import {
  alwaysHolds,
  blockAt,
  buildText,
  ConditionSpec,
  crossesBlock,
  describeBlock,
  hasTextSyntax,
  longestVariant,
  readBlock,
  scanText,
  TextBlock,
  TextScan,
} from './text-condition-grammar';
import {SbModalService} from '@app/_components/unified/sb-modal/sb-modal.service';

/** A variable a rule can test, as the dialog shows it. */
export interface ConditionVariable {
  /** Simple key, without #. */
  key: string;
  name: string;
  description: string;
  sample: string;
  valueType: string[];
  /** The only values it takes, when it has a fixed set. */
  options: Array<{ value: string; label: string }>;
}

export interface TextConditionContext {
  spec: ConditionSpec;
  variables: ConditionVariable[];
  /** By upper-cased simple key: the render matches keys case-insensitively. */
  byKey: Map<string, ConditionVariable>;
}

type TextField = HTMLInputElement | HTMLTextAreaElement;

/**
 * Conditional text in plain-text fields: the condition spec and variables, reading a field's
 * `{% if %}` blocks, and the dialog that writes or edits one at the caret.
 */
@Injectable({ providedIn: 'root' })
export class SbTextConditionService {
  private readonly sbModal = inject(SbModalService);
  private readonly toastr = inject(ToastrService);
  private readonly variableService = inject(VariableService);
  private readonly variableStore = inject<Store<IVariableState>>(Store);

  private spec$: Observable<ConditionSpec | null> | null = null;
  private fetchedVariables$: Observable<ConditionVariable[]> | null = null;
  private systemicVariables$: Observable<ConditionVariable[]> | null = null;

  /**
   * The spec and variables, or null when the server has no text conditions to offer.
   *
   * @param scope 'systemic' for a system email's field: only the system variables
   */
  context(scope: VariableScope = 'campaign'): Observable<TextConditionContext | null> {
    return this.spec().pipe(
      switchMap((spec) => (spec ? this.variables(scope).pipe(map((variables) => this.contextOf(spec, variables))) : of(null))),
    );
  }

  /** Every block and problem of a field's text, checked against the variables when they are known. */
  analyze(text: string, context: TextConditionContext): TextScan {
    return scanText(text || '', context.spec, {
      knownVariables: context.variables.length ? context.variables.map((variable) => variable.key) : null,
    });
  }

  /**
   * True when every recipient's text keeps a variable (`#Key#`): outside every condition, or in
   * each branch of an if/else. A block the render removes (malformed, never closed, an unknown
   * variable) never counts. Without the spec (not loaded, or an older server) it is a plain search.
   */
  alwaysHas(text: string, token: string, context: TextConditionContext | null): boolean {
    const value = text || '';
    if (!context || !value.includes(context.spec.text.open)) {
      return hasVariableToken(value, token);
    }
    return alwaysHolds(value, this.analyze(value, context), (part) => hasVariableToken(part, token));
  }

  /** True when a field's text holds conditional text (a block, readable or not). */
  hasConditions(text: string, context: TextConditionContext | null): boolean {
    const value = text || '';
    return !!context && value.includes(context.spec.text.open) && scanText(value, context.spec).blocks.length > 0;
  }

  /**
   * The longest text any recipient gets from a field: each condition replaced by its longest
   * branch (by `measure`, characters by default). The text itself when it has no conditions.
   */
  longestVariant(text: string, context: TextConditionContext | null, measure?: (part: string) => number): string {
    const value = text || '';
    if (!context || !value.includes(context.spec.text.open)) {
      return value;
    }
    return longestVariant(value, this.analyze(value, context), measure);
  }

  /** The friendly name of a simple key (`#Key#` when the panel does not know it). */
  nameOf(context: TextConditionContext, key: string): string {
    const variable = context.byKey.get(String(key).toUpperCase());
    return variable ? variable.name : `#${key}#`;
  }

  /** A readable block in plain words, or null. */
  describe(text: string, block: TextBlock, context: TextConditionContext): string | null {
    const content = readBlock(text, block);
    return content ? describeBlock(content, context.spec, (key) => this.nameOf(context, key)) : null;
  }

  /**
   * Opens the dialog for the selection in a field: it edits the block the caret is in, or wraps
   * the selected words in a new one. On save the field's text is rewritten, the caret goes after
   * the block and an `input` event tells the field's bindings.
   *
   * @param scope the variables the field can use (see context())
   * @returns the field's new text, or null when nothing changed
   */
  async edit(field: TextField, start: number, end: number, scope: VariableScope = 'campaign'): Promise<string | null> {
    const context = await firstValueFrom(this.context(scope));
    if (!context || !field) {
      return null;
    }
    const text = field.value || '';
    const scan = this.analyze(text, context);
    if (crossesBlock(scan, start, end)) {
      this.toastr.warning('Select words inside one conditional text, or a whole one, then try again.');
      return null;
    }
    const block = blockAt(scan, start, end);
    const content = block ? readBlock(text, block) : null;
    if (block && !content) {
      const problem = scan.problems.find((item) => item.start >= block.start && item.end <= block.end);
      this.toastr.warning(problem ? problem.message : 'This conditional text cannot be read.', 'Fix it in the text first');
      if (problem) {
        this.select(field, problem.start, problem.end);
      }
      return null;
    }

    const ref = this.sbModal.openComponent(SbTextConditionDialogComponent, {
      centered: true,
      scrollable: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: true,
      windowClass: 'sb-text-condition-modal',
    }, {
      spec: context.spec,
      variables: context.variables,
      content,
      selectedText: block ? '' : text.slice(start, end),
    });

    let result: SbTextConditionDialogResult | null = null;
    try {
      result = await ref.result;
    } catch {
      result = null;
    }
    if (!result) {
      field.focus();
      return null;
    }
    const replacement = 'removed' in result ? content.cases[0].text : buildText(result.content, context.spec);
    const from = block ? block.start : start;
    const to = block ? block.end : end;
    const next = text.slice(0, from) + replacement + text.slice(to);
    field.value = next;
    this.select(field, from + replacement.length, from + replacement.length);
    field.dispatchEvent(new Event('input', { bubbles: true }));
    return next;
  }

  /** Focuses a field with a range selected (shows where a problem is). */
  select(field: TextField, start: number, end: number): void {
    if (!field) {
      return;
    }
    field.focus();
    try {
      field.setSelectionRange(start, end);
    } catch {
      // Not a text control (e.g. type=number): nothing to select.
    }
  }

  private spec(): Observable<ConditionSpec | null> {
    if (!this.spec$) {
      this.spec$ = this.variableService.conditionSpec().pipe(
        map((response: any) => {
          const spec = response && response.success !== false ? (response.data ?? response) : null;
          return hasTextSyntax(spec) ? (spec as ConditionSpec) : null;
        }),
        catchError(() => {
          // Ask again next time: a failed request must not switch the feature off for the session.
          this.spec$ = null;
          return of(null);
        }),
        shareReplay(1),
      );
    }
    return this.spec$;
  }

  /**
   * The variables store's list when a page has loaded it, else the same endpoint read once. The
   * system variables are read once on their own: the store holds a page's campaign list, and other
   * pages read it without loading it themselves.
   */
  private variables(scope: VariableScope): Observable<ConditionVariable[]> {
    if (scope === 'systemic') {
      if (!this.systemicVariables$) {
        this.systemicVariables$ = this.variableService.list({systemic_only: true}).pipe(
          map((response: any) => this.rowsOf(response && response.success ? response.data : null)),
          catchError(() => {
            this.systemicVariables$ = null;
            return of([] as ConditionVariable[]);
          }),
          shareReplay(1),
        );
      }
      return this.systemicVariables$;
    }
    return this.variableStore.pipe(
      select(getVariables),
      take(1),
      switchMap((stored) => {
        const rows = this.rowsOf(stored);
        if (rows.length) {
          return of(rows);
        }
        if (!this.fetchedVariables$) {
          this.fetchedVariables$ = this.variableService.list({}).pipe(
            map((response: any) => this.rowsOf(response && response.success ? response.data : null)),
            catchError(() => {
              this.fetchedVariables$ = null;
              return of([] as ConditionVariable[]);
            }),
            shareReplay(1),
          );
        }
        return this.fetchedVariables$;
      }),
    );
  }

  private rowsOf(list: unknown): ConditionVariable[] {
    if (!list || typeof list !== 'object') {
      return [];
    }
    const rows: ConditionVariable[] = [];
    Object.values(list as Record<string, any>).forEach((item) => {
      if (!item || typeof item !== 'object' || !item.simpleKey) {
        return;
      }
      const valueType: string[] = Array.isArray(item.valueType) ? item.valueType : [];
      const formats: string[] = Array.isArray(item.supportedFormats) ? item.supportedFormats : [];
      // Same rows as the variable picker offers a plain-text field.
      if (valueType.includes('ignore') || (formats.length && !formats.includes('plain_text'))) {
        return;
      }
      let sample = item.sample;
      if (sample && typeof sample === 'object') {
        sample = sample.plain_text || sample.html || '';
      }
      rows.push({
        key: String(item.simpleKey),
        name: String(item.title || item.simpleKey),
        description: String(item.description || ''),
        sample: sample == null ? '' : String(sample),
        valueType,
        options: Array.isArray(item.options)
          ? item.options
            .filter((option) => option != null)
            .map((option) => (typeof option === 'object'
              ? { value: String(option.value ?? ''), label: String(option.label ?? option.value ?? '') }
              : { value: String(option), label: String(option) }))
          : [],
      });
    });
    return rows.sort((a, b) => a.name.localeCompare(b.name));
  }

  private contextOf(spec: ConditionSpec, variables: ConditionVariable[]): TextConditionContext {
    const byKey = new Map<string, ConditionVariable>();
    variables.forEach((variable) => byKey.set(variable.key.toUpperCase(), variable));
    return { spec, variables, byKey };
  }
}
